import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs/internal/Observable';
import { ApiErrorService } from '../api-error.service';
import { AccountService } from '../user/account.service';
import { TxService } from '../shared/tx.service';

export interface SmartHttpErrorOptions {
  suppressGlobalErrorFor4xx?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SmartHttpService {
  constructor(
    private http: HttpClient,
    private apiErrorService: ApiErrorService,
    private accountservice: AccountService,
    private txService: TxService
  ) {}
  public get httpOptions(): HttpHeaders {
    return this.txService.getHeaders(this.accountservice.token);
  }

  public set httpOptions(value: HttpHeaders) {
    this.httpOptions = value;
  }

  get<T>(url: string, options?: any): Observable<T> {
    return this.http.get<T>(url, {
      headers: options || this.httpOptions,
    });
  }

  put<T>(url: string, payload: any, options?: any): Observable<T> {
    return this.http.put<T>(url, payload, {
      headers: options || this.httpOptions,
    });
  }

  delete(url: string, options?: any) {
    return this.http.delete(url, options);
  }

  private handleApiError(err: any, errorOptions?: SmartHttpErrorOptions): void {
    const status = err?.status ?? err?.error?.status ?? null;
    const isClientError =
      typeof status === 'number' && status >= 400 && status < 500;
    if (errorOptions?.suppressGlobalErrorFor4xx && isClientError) {
      return;
    }
    this.apiErrorService.updateValue(true);
    this.apiErrorService.setErrorStatus(status);
  }

  getWithErorrHandler<T>(
    url: string,
    options?: any,
    errorOptions?: SmartHttpErrorOptions
  ): Observable<T> {
    return this.get<T>(url, options).pipe(
      tap({
        next: (response: any) => {},
        error: (err: any) => this.handleApiError(err, errorOptions),
      })
    );
  }

  putWithErorrHandler(
    url: string,
    payload: any,
    options?: any,
    errorOptions?: SmartHttpErrorOptions
  ): Observable<HttpEvent<any>> {
    return this.put<Observable<HttpEvent<any>>>(url, payload, options).pipe(
      tap({
        next: (response: any) => {},
        error: (err: any) => this.handleApiError(err, errorOptions),
      })
    );
  }

  post<T>(url: string, payload: any, options?: any): Observable<T> {
    return this.http.post<T>(url, payload, {
      headers: options || this.httpOptions,
    });
  }

  postWithErorrHandler<T>(
    url: string,
    payload: any,
    options?: any,
    errorOptions?: SmartHttpErrorOptions
  ): Observable<T> {
    return this.post<T>(url, payload, options).pipe(
      tap({
        next: (response: any) => {},
        error: (err: any) => this.handleApiError(err, errorOptions),
      })
    );
  }
}
